// Server Health Panel — disk space, recorder status, engine health
//
// Provides at-a-glance confidence that all data recorders are functioning.
// Shows disk usage, per-symbol tick recorder freshness, GEX snapshot age
// (from DB), OPRA ratio age, and engine uptime. Recorder rows mirror the
// tick-recorder style: `VALUE · MMDDYY`, STALE · age when unhealthy.
// (Automation timer rows removed 2026-09-02 — recorder rows above already
// prove the pipelines those timers feed; disk growth is caught by the
// watchdog itself, not by watching the watchdog.)

import { useQuery } from "@tanstack/react-query";
import { apiVps } from "../api/client";

const REFRESH_MS = 60_000;

interface DiskInfo {
  total_gb: number;
  used_gb: number;
  free_gb: number;
  pct_used: number;
}

interface TickRecorder {
  status: string;
  age_min: number;
  size_bytes: number;
  file_count: number;
  last_date?: string;
}

interface GexRecorder {
  status: string;
  age_min: number;
  latest_date: string;
  symbol: string;
  net_delta?: number;
  net_delta_fmt?: string;
  total_gamma_fmt?: string;
  total_gamma: number;
  put_call_ratio: number;
}

interface OpraRecorder {
  status: string;
  age_min: number;
  latest_date: string;
  size_bytes: number;
}

interface EngineInfo {
  pid: number;
  uptime_seconds: number;
}

interface SystemStatus {
  ts: string;
  disk: DiskInfo;
  recorders: {
    tick?: Record<string, TickRecorder>;
    gex_snapshot?: GexRecorder;
    opra_pc_ratio?: OpraRecorder;
  };
  engine: EngineInfo;
  timers: Record<string, string>;
}

function fmtAge(minutes: number | undefined): string {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  if (h < 48) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function statusColor(status: string | undefined): string {
  if (!status || status === "error") return "var(--bad)";
  if (status === "active") return "var(--good)";
  if (status === "rolled") return "var(--fg-muted)";
  if (status === "stale") return "var(--bad)";
  return "var(--warn)";
}

function diskColor(pct: number): string {
  if (pct > 90) return "var(--bad)";
  if (pct > 75) return "var(--warn)";
  return "var(--good)";
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: 8, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
      color: "var(--fg-muted)", padding: "3px 0 1px 0", borderTop: "1px solid var(--border)",
      marginTop: 2, fontFamily: "var(--sans)", flexShrink: 0,
    }}>
      {label}
    </div>
  );
}

function StatRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1px 0", fontSize: 10 }}>
      <span style={{ color: "var(--fg-muted)", fontSize: 9, letterSpacing: "0.02em" }}>{label}</span>
      <span className="mono" style={{ fontWeight: 600, color: color ?? "var(--fg-primary)", fontSize: 10, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </span>
    </div>
  );
}

export function ServerHealthPanel() {
  const { data, isLoading } = useQuery<SystemStatus>({
    queryKey: ["system", "status"],
    queryFn: () => apiVps<SystemStatus>("/system/status"),
    refetchInterval: REFRESH_MS,
  });

  return (
    <div className="card">
      <h2 style={{ flexShrink: 0 }}>Server Health</h2>
      <div className="scroll-vert">
        {isLoading && (
          <div className="muted" style={{ fontSize: 9, padding: "4px 0" }}>Loading...</div>
        )}
        {!data && !isLoading && (
          // <div className="muted" style={{ fontSize: 9, padding: "4px 0" }}>Awaiting engine restart</div>
          <div className="muted small">awaiting engine restart</div>
        )}
        {data && (
          <>
            <SectionHeader label="Disk" />
            <StatRow
              label="Used / Free"
              value={`${data.disk.used_gb}G / ${data.disk.free_gb}G (${data.disk.pct_used}%)`}
              color={diskColor(data.disk.pct_used)}
            />

            <SectionHeader label="Tick Recorder" />
            {data.recorders.tick && Object.entries(data.recorders.tick).sort().map(([sym, info]) => {
              const displayValue = info.status === "rolled"
                ? `ROLLED · ${info.last_date?.slice(2) ?? ""}`
                : info.status === "active"
                  ? `${(info.size_bytes / 1e6).toFixed(1)}MB · ${info.last_date?.slice(2) ?? ""}`
                  : `STALE · ${fmtAge(info.age_min)}`;
              return (
                <StatRow
                  key={sym}
                  label={sym}
                  value={displayValue}
                  color={statusColor(info.status)}
                />
              );
            })}

            <SectionHeader label="GEX Snapshot" />
            {data.recorders.gex_snapshot && (() => {
              const g = data.recorders.gex_snapshot!;
              const dateCompact = g.latest_date?.replaceAll("-", "")?.slice(2) ?? "";
              const displayValue = g.status === "active"
                ? `${g.total_gamma_fmt ?? `$${(g.total_gamma / 1e9).toFixed(1)}B`} · PCR ${g.put_call_ratio.toFixed(2)} · ${dateCompact}`
                : `STALE · ${fmtAge(g.age_min)}`;
              // Preserve the old per-value PCR threshold coloring (>1.5 warn, >2.0 bad)
              // on the merged row; recorder status color otherwise.
              const pcrColor = g.status === "active"
                ? (g.put_call_ratio > 2 ? "var(--bad)" : g.put_call_ratio > 1.5 ? "var(--warn)" : undefined)
                : undefined;
              return (
                <StatRow
                  label={`${g.symbol} GEX`}
                  value={displayValue}
                  color={pcrColor ?? statusColor(g.status)}
                />
              );
            })()}

            <SectionHeader label="OPRA PC Ratio" />
            {data.recorders.opra_pc_ratio && (() => {
              const o = data.recorders.opra_pc_ratio!;
              return (
                <StatRow label="Latest" value={`${o.latest_date} · ${fmtAge(o.age_min)} ago`} color={statusColor(o.status)} />
              );
            })()}

            <SectionHeader label="Engine" />
            <StatRow label="Uptime" value={data.engine.uptime_seconds > 0 ? fmtAge(Math.floor(data.engine.uptime_seconds / 60)) : "—"} />
            <StatRow label="PID" value={String(data.engine.pid)} />
          </>
        )}
      </div>
    </div>
  );
}